/**
 * Visibility rule for the patient-overview "ANC visits" tab.
 *
 * Per PNE-279 product spec (Chris):
 *   "Visible if count of ANC records in the last 365 days >= 1 AND
 *    current date < EDD + 12 weeks"
 *
 * The pregnancy is considered "relevant to mum's health" until 12 weeks past
 * the expected delivery date. We use the most recent ANC encounter as the
 * source of EDD, falling back to LMP + 280 days, falling back to
 * createdAt + 40 weeks (so a freshly-saved encounter that hasn't filled EDD
 * yet still shows the tab).
 */

import { addDays, differenceInCalendarDays, isAfter } from "date-fns";
import { PREGNANCY_DURATION_DAYS } from "./gestational-age";

const ACTIVE_WINDOW_DAYS = 365;
const POSTPARTUM_WINDOW_DAYS = 12 * 7; // 12 weeks

type AncEncounterForVisibility = {
  createdAt: string;
  dueDate?: string | null;
  lastMenstrualPeriod?: string | null;
};

function effectiveEdd(encounter: AncEncounterForVisibility): Date {
  if (encounter.dueDate) return new Date(encounter.dueDate);
  if (encounter.lastMenstrualPeriod) {
    return addDays(
      new Date(encounter.lastMenstrualPeriod),
      PREGNANCY_DURATION_DAYS,
    );
  }
  return addDays(new Date(encounter.createdAt), PREGNANCY_DURATION_DAYS);
}

export function shouldShowAncVisitsTab(
  encounters: readonly AncEncounterForVisibility[],
  now: Date = new Date(),
): boolean {
  const recent = encounters.filter(
    (e) =>
      differenceInCalendarDays(now, new Date(e.createdAt)) <=
      ACTIVE_WINDOW_DAYS,
  );
  if (recent.length === 0) return false;

  const mostRecent = recent.reduce((a, b) =>
    isAfter(new Date(a.createdAt), new Date(b.createdAt)) ? a : b,
  );

  const cutoff = addDays(effectiveEdd(mostRecent), POSTPARTUM_WINDOW_DAYS);
  return now.getTime() < cutoff.getTime();
}
